import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Container, IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useSettingsStore, useStatusDialogStore, useStore } from '@core/store';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import { getMenuData } from '@core/data/menuData';
import { ActiveLicenses, Role } from '@core/types';
import { FCSecurePage, useRegisterRefresh, useMyShopsPersistence } from '@/components/shared/navigation/securePage';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable';
import AllShopsSelector from '@/components/selectors/AllShopsSelector';
import { apiClient } from '@core/api';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import MobileShopCard, { ShopRow, OperationModeState, CurrentStateCell } from './components/MobileShopCard';
import { HEALTH_ORDER, toHealthKey } from '@core/utils/statusUtils';
import { sortWithAlphabetTiebreaker } from '@core/utils/sortUtils';
import HealthStatusCell from './components/HealthStatusCell';
import DeviceSubTable from './components/DeviceSubTable';
import { operationModeService } from '@/services';
import { equalsIgnoreCase } from '@core/utils';
import { ItemStatus } from '@features/shopautomation/types.ts';
import { PersistKeys } from '@core/store/slices/persistenceSlice';

const COL_NAME = 250;
const COL_STATE = 180;
const COL_CURRENT_STATE = 360;
const COL_ACTION = 160;

interface ShopStatusResponse {
    id: string;
    name: string;
    shopId: string;
    street: string;
    city: string;
    postalCode: string;
    isProduction: boolean;
    healthStatusType: string;
    notHealthyDevices: string[];
    operationModeState: OperationModeState;
    items: ItemStatus[];
}

const ActionCell: React.FC<{
    row: ShopRow;
    expandedRows: (string | number)[];
    isSuperAdmin: boolean;
    onToggle: (rowKey: string | number) => void;
    onShopSetup: (shopEntityId: string) => void;
    t: (key: string) => string;
}> = ({ row, expandedRows, isSuperAdmin, onToggle, onShopSetup, t }) => {
    const isExpanded = expandedRows.includes(row.id);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip
                title={isExpanded ? t('collapseRowTooltip') : t('expandRowTooltip')}
                classes={{ tooltip: 'page-title-tooltip' }}
            >
                <IconButton
                    size="small"
                    className="scc-icon-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle(row.id);
                    }}
                >
                    <i
                        className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                        style={{ lineHeight: 1, display: 'block' }}
                    />
                </IconButton>
            </Tooltip>
            {isSuperAdmin && (
                <Tooltip title={t('shopSettingsTooltip')} classes={{ tooltip: 'page-title-tooltip' }}>
                    <IconButton
                        size="small"
                        className="scc-icon-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShopSetup(row.id);
                        }}
                    >
                        <i className="bi bi-gear" style={{ lineHeight: 1, display: 'block' }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

const MyShops: FCSecurePage = () => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();
    const { hasLicense, hasRole } = useAuthStore();
    const { showStatusLoading, showStatusSuccess, showStatusError } = useStatusDialogStore();
    const navigate = useNavigate();
    const setPersisted = useStore((s) => s.setPersisted);

    // My Shops always defaults to all accessible shops and never inherits a previously persisted shop.
    const { selectedShopIds, setSelectedShopIds } = useMyShopsPersistence();
    const [expandedRows, setExpandedRows] = useState<(string | number)[]>([]);
    const [loading, setLoading] = useState(false);
    const [shopStatusData, setShopStatusData] = useState<ShopStatusResponse[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const reloadMinutes = 5;
    const hasShopAutomation = hasLicense(ActiveLicenses.ShopAutomation);
    const isSuperAdmin = hasRole(Role.SuperAdmin);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const statusResponse = await apiClient.get<ShopStatusResponse[]>('shop/status');
            setShopStatusData(statusResponse.data);
            setLastUpdate(DateTime.now());
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const unsubscribe = eventBus.on(EventTypes.ORGANISATION_CHANGED, () => {
            loadData();
        });
        return unsubscribe;
    }, [loadData]);

    useEffect(() => {
        const startTimer = () => {
            reloadTimerRef.current = setTimeout(
                () => {
                    loadData();
                    startTimer();
                },
                reloadMinutes * 60 * 1000
            );
        };
        startTimer();
        return () => {
            if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
        };
    }, [loadData]);

    useRegisterRefresh(loadData, selectedShopIds.length === 0);

    const handleShopSelectorChange = useCallback(
        (val: string[]) => {
            setSelectedShopIds(val ?? []);
            loadData();
        },
        [loadData, setSelectedShopIds]
    );

    const allRows = useMemo(
        (): ShopRow[] =>
            shopStatusData.map((shop) => ({
                id: shop.id,
                name: shop.name ?? '',
                street: shop.street ?? '',
                city: shop.city ?? '',
                shopId: shop.shopId ?? '',
                healthStatus: toHealthKey(shop.healthStatusType),
                healthOrder: HEALTH_ORDER[toHealthKey(shop.healthStatusType)],
                tooltipMessage: shop.notHealthyDevices?.join(', ') ?? '',
                operationModeState: shop.operationModeState ?? null,
                items: shop.items ?? []
            })),
        [shopStatusData]
    );

    const filteredRows = useMemo(
        () => allRows.filter((r) => selectedShopIds.includes(r.id)),
        [allRows, selectedShopIds]
    );

    const handleExpandToggle = useCallback((rowKey: string | number) => {
        setExpandedRows((prev) => {
            if (prev.includes(rowKey)) return prev.filter((id) => id !== rowKey);
            return [...prev, rowKey];
        });
    }, []);

    const handleShopSetup = useCallback(
        (shopEntityId: string) => {
            setPersisted(PersistKeys.SHOP_IDS, [shopEntityId]);
            navigate('/manage-shop/shopsetup');
        },
        [navigate, setPersisted]
    );

    const onRebootDevice = useCallback(
        async (item: ItemStatus): Promise<void> => {
            const rebootCommand = item.commands?.find((cmd) => equalsIgnoreCase(cmd, 'reboot'));
            if (!rebootCommand || !item.deviceOrServiceType || !item.shopAutomationId) return;
            const gatewayDeviceId = item.deviceOrServiceType === 'gateway' ? item.name : item.gatewayId;
            if (!gatewayDeviceId) return;

            const [deviceCommand, deviceValue] = rebootCommand.split(':');

            showStatusLoading({
                heading: t('commandDevice', { command: deviceCommand }),
                message: t('sendingCommand'),
                closable: false
            });

            try {
                await operationModeService.sendDeviceCommand(
                    gatewayDeviceId,
                    item.shopAutomationId,
                    item.deviceOrServiceType,
                    item.name || '',
                    deviceCommand,
                    deviceValue
                );
                showStatusSuccess({
                    heading: t('commandRequested', { command: deviceCommand }),
                    message: t('commandSuccessMessage', { command: deviceCommand, device: item.name })
                });
            } catch (error) {
                showStatusError({
                    heading: t('commandFailed', { command: deviceCommand }),
                    message: t('commandFailedMessage', { command: deviceCommand, device: item.name }),
                    closable: true,
                    actions: {
                        confirmText: 'tryAgain',
                        onConfirm: () => onRebootDevice(item)
                    }
                });
                throw error;
            }
        },
        [t, showStatusLoading, showStatusSuccess, showStatusError]
    );

    const columns = useMemo((): DataTableColumn<ShopRow>[] => {
        const cols: DataTableColumn<ShopRow>[] = [
            {
                value: 'name',
                text: t('shopInfo'),
                sortable: true,
                width: COL_NAME,
                render: (row) => (
                    <div className="scc-cell-slot">
                        <span className="scc-shop-name">{row.name}</span>
                        <div className="scc-shop-address">
                            {row.street && <span className="scc-shop-address-line">{row.street}</span>}
                            {row.city && <span className="scc-shop-address-line">{row.city}</span>}
                            {isSuperAdmin && row.shopId && (
                                <span className="scc-shop-address-line scc-shop-id">ID: {row.shopId}</span>
                            )}
                        </div>
                    </div>
                )
            }
        ];

        cols.push({
            value: 'healthOrder',
            text: t('state'),
            sortable: true,
            width: COL_STATE,
            render: (row) => (
                <div className="scc-cell-slot">
                    <Box />
                    <Box sx={{ alignSelf: 'flex-start' }}>
                        <HealthStatusCell healthStatus={row.healthStatus} variant="shop" />
                    </Box>
                </div>
            )
        });

        if (hasShopAutomation) {
            cols.push({
                value: 'currentState',
                text: t('currentState'),
                sortable: false,
                width: COL_CURRENT_STATE,
                render: (row) => {
                    const hasMode = !!row.operationModeState?.currentOperationMode;
                    return (
                        <div className="scc-cell-slot">
                            <Box />
                            {hasMode && <CurrentStateCell state={row.operationModeState!} t={t} />}
                        </div>
                    );
                }
            });
        }

        cols.push({
            value: 'action',
            text: t('action'),
            sortable: false,
            width: COL_ACTION,
            isAction: true,
            align: 'center',
            render: (row) => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: '90px'
                    }}
                >
                    <ActionCell
                        row={row}
                        expandedRows={expandedRows}
                        isSuperAdmin={isSuperAdmin}
                        onToggle={handleExpandToggle}
                        onShopSetup={handleShopSetup}
                        t={t}
                    />
                </Box>
            )
        });

        return cols;
    }, [isSuperAdmin, hasShopAutomation, expandedRows, handleExpandToggle, handleShopSetup, t]);

    let tableClassName = 'data-table__wrapper wcn-table--spaced scc-table-wrapper';
    if (filteredRows.length > 1) tableClassName += ' scc-multi-shop';

    let content = (
        <Box className={tableClassName}>
            <NewWcnTable
                columns={columns}
                data={filteredRows}
                loading={loading}
                noDataMessage={t('noData')}
                size="medium"
                sorting={{ defaultSortBy: 'healthOrder' }}
                customSort={sortWithAlphabetTiebreaker}
                onRowClick={(row) => handleExpandToggle(row.id)}
                expansion={{
                    expandedRows,
                    onExpandToggle: handleExpandToggle,
                    getRowKey: (row) => row.id,
                    showExpandIconCondition: () => false,
                    renderExpandedContent: (row) => {
                        return <DeviceSubTable items={row.items} onReboot={onRebootDevice} />;
                    }
                }}
            />
        </Box>
    );

    if (isMobile) {
        content = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filteredRows.map((row) => (
                    <MobileShopCard
                        key={row.id}
                        row={row}
                        isExpanded={expandedRows.includes(row.id)}
                        isSuperAdmin={isSuperAdmin}
                        hasShopAutomation={hasShopAutomation}
                        onToggle={handleExpandToggle}
                        onShopSetup={handleShopSetup}
                        t={t}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="bi bi-shop-window" style={{ fontSize: '16px' }} />}
                title={t('myShops')}
                menus={getMenuData()}
                currentRoute={{ path: '/myshops' }}
                t={t}
                hasAutoRefresh={true}
                lastUpdate={lastUpdate}
            />

            {/* Filter Section - Desktop Only */}
            <Box
                className="scc-filter-section"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 3,
                    px: isMobile ? 2 : 3,
                    pt: isMobile ? 3 : 1,
                    pb: 1
                }}
            >
                <AllShopsSelector value={selectedShopIds} onChange={handleShopSelectorChange} label={t('shop')} />
            </Box>

            <Container maxWidth={false} sx={{ py: 3 }}>
                {content}
            </Container>
        </Box>
    );
};

MyShops.displayName = 'MyShops';
export default MyShops;
